import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import {
	Box,
	ToggleButtonGroup,
	ToggleButton,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import LinkIcon from "@mui/icons-material/Link";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import TableChartIcon from "@mui/icons-material/TableChart";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DeleteIcon from "@mui/icons-material/Delete";
import GridOffIcon from "@mui/icons-material/GridOff";

const extensions = [
	StarterKit.configure({
		// Exclude image-related if any; StarterKit doesn't include Image by default
	}),
	Underline,
	Link.configure({
		openOnClick: false,
		HTMLAttributes: {
			style: "color: #1976d2; text-decoration: underline; cursor: pointer;",
		},
	}),
	Table.configure({
		resizable: true,
	}),
	TableRow,
	TableCell,
	TableHeader,
];

interface SkillDocumentCommentBoxProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    /** When true, uses compact resizable layout (for PMS). When false, uses original fixed height (for Skill Set). */
    compact?: boolean;
}

const SkillDocumentCommentBox: React.FC<SkillDocumentCommentBoxProps> = ({
    value,
    onChange,
    placeholder = "Add a comment...",
    disabled = false,
    compact = false,
}) => {
	const [linkDialogOpen, setLinkDialogOpen] = useState(false);
	const [linkUrl, setLinkUrl] = useState("");

	const editor = useEditor({
		extensions,
		content: value,
		editable: !disabled,
		onUpdate: ({ editor }) => {
			onChange(editor.getHTML());
		},
		editorProps: {
			attributes: {
				"data-placeholder": placeholder,
				style: `min-height: ${compact ? 80 : 120}px; outline: none;`,
			},
		},
	});

	// Sync external value changes
	useEffect(() => {
		if (editor && value !== editor.getHTML()) {
			editor.commands.setContent(value, false );		}
	}, [value, editor]);

	// Update editable when disabled changes
	useEffect(() => {
		if (editor) {
			editor.setEditable(!disabled);
		}
	}, [disabled, editor]);

	if (!editor) {
		return null;
	}

    return (
        <Box
            sx={{
				border: "1px solid rgba(0, 0, 0, 0.60)",
				borderRadius: 1,
				...(compact
					? {
							resize: "vertical",
							minHeight: 80,
							maxHeight: 400,
							overflowY: "auto",
					  }
					: { overflow: "hidden" }),

				"& .ProseMirror": {
                    minHeight: compact ? 80 : 120,
					padding: 2,
                    fontSize: 14,
					overflowX: "auto",
				},
				"& .ProseMirror p.is-editor-empty:first-child::before": {
					content: "attr(data-placeholder)",
					float: "left",
					color: "#3c3c3c",
					pointerEvents: "none",
					height: 0,
				},
				"& .ProseMirror table": {
					borderCollapse: "collapse",
					width: "max-content",
					minwidth: "100%",
					margin: "8px 0",
				},
				"& .ProseMirror th, & .ProseMirror td": {
					border: "1px solid #ddd",
					padding: "8px",
					minWidth: 100,
				},
				"& .ProseMirror th": {
					backgroundColor: "#f5f5f5",
                },
            }}
        >
		{!disabled && (
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 0.5,
					p: 1,
					borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
					backgroundColor: "#fafafa",
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
					<ToggleButtonGroup size="small" sx={{ flexWrap: "wrap" }}>
						<ToggleButton
							selected={editor.isActive("bold")}
							onClick={() => editor.chain().focus().toggleBold().run()}
							value="bold"
						>
							<FormatBoldIcon fontSize="small" />
						</ToggleButton>
						<ToggleButton
							selected={editor.isActive("italic")}
							onClick={() => editor.chain().focus().toggleItalic().run()}
							value="italic"
						>
							<FormatItalicIcon fontSize="small" />
						</ToggleButton>
						<ToggleButton
							selected={editor.isActive("underline")}
							onClick={() => editor.chain().focus().toggleUnderline().run()}
							value="underline"
						>
							<FormatUnderlinedIcon fontSize="small" />
						</ToggleButton>
						<ToggleButton
							selected={editor.isActive("link")}
							onClick={() => {
								const prevHref = editor.getAttributes("link").href;
								setLinkUrl(prevHref || "");
								setLinkDialogOpen(true);
							}}
							value="link"
						>
							<LinkIcon fontSize="small" />
						</ToggleButton>
						<ToggleButton
							selected={editor.isActive("bulletList")}
							onClick={() => editor.chain().focus().toggleBulletList().run()}
							value="bullet"
						>
							<FormatListBulletedIcon fontSize="small" />
						</ToggleButton>
						<ToggleButton
							selected={editor.isActive("orderedList")}
							onClick={() => editor.chain().focus().toggleOrderedList().run()}
							value="ordered"
						>
							<FormatListNumberedIcon fontSize="small" />
						</ToggleButton>
						<Tooltip title="Insert table">
							<ToggleButton
								onClick={() =>
									editor.chain().focus().insertTable({ rows: 3, cols: 3 }).run()
								}
								value="table"
							>
								<TableChartIcon fontSize="small" />
							</ToggleButton>
						</Tooltip>
						<Tooltip title="Add row below">
							<span>
								<ToggleButton
									disabled={!editor.isActive("table")}
									onClick={() => editor.chain().focus().addRowAfter().run()}
									value="addRow"
								>
									<PlaylistAddIcon fontSize="small" />
								</ToggleButton>
							</span>
						</Tooltip>
						<Tooltip title="Add column after">
							<span>
								<ToggleButton
									disabled={!editor.isActive("table")}
									onClick={() => editor.chain().focus().addColumnAfter().run()}
									value="addColumn"
								>
									<ViewColumnIcon fontSize="small" />
								</ToggleButton>
							</span>
						</Tooltip>
						<Tooltip title="Delete row">
							<span>
								<ToggleButton
									disabled={!editor.isActive("table")}
									onClick={() => editor.chain().focus().deleteRow().run()}
									value="deleteRow"
								>
									<DeleteIcon fontSize="small" />
								</ToggleButton>
							</span>
						</Tooltip>
						<Tooltip title="Delete column">
							<span>
								<ToggleButton
									disabled={!editor.isActive("table")}
									onClick={() => editor.chain().focus().deleteColumn().run()}
									value="deleteColumn"
								>
									<DeleteIcon fontSize="small" />
								</ToggleButton>
							</span>
						</Tooltip>
						<Tooltip title="Delete table">
							<span>
								<ToggleButton
									disabled={!editor.isActive("table")}
									onClick={() => editor.chain().focus().deleteTable().run()}
									value="deleteTable"
								>
									<GridOffIcon fontSize="small" />
								</ToggleButton>
							</span>
						</Tooltip>
					</ToggleButtonGroup>
				</Box>
			)}
			<EditorContent editor={editor} />
			<Dialog
				open={linkDialogOpen}
				onClose={() => setLinkDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Insert Link</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="URL"
						type="url"
						fullWidth
						variant="outlined"
						value={linkUrl}
						onChange={(e) => setLinkUrl(e.target.value)}
						placeholder="https://example.com"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
					<Button
					onClick={() => {
						const raw = linkUrl.trim();
						if (raw) {
							const href =
								/^(https?:\/\/|mailto:|\/\/)/i.test(raw)
									? raw
									: `https://${raw}`;
							editor.chain().focus().setLink({ href }).run();
						}
						setLinkDialogOpen(false);
						setLinkUrl("");
					}}
						variant="contained"
					>
						Insert
					</Button>
				</DialogActions>
			</Dialog>
        </Box>
    );
};

export default SkillDocumentCommentBox;